import { MixpanelEvents } from '~/core/service/mixpanel';

/**
 * Composable for managing user identity tracking and session stitching in Mixpanel
 *
 * This composable helps ensure that anonymous browsing data is properly
 * associated with authenticated user data after login.
 */
export function useIdentityTracking() {
	const { $mixpanel } = useNuxtApp();
	const { user } = useUserSession();

	/**
	 * Stitch anonymous session with authenticated user session
	 *
	 * This function should be called when a user successfully logs in
	 * or completes registration.
	 *
	 * @param {object} params - Parameters for session stitching
	 * @param {string} params.userId - The user's ID or email
	 * @param {string} params.userName - The user's name
	 * @param {string} params.userEmail - The user's email
	 * @param {string} params.userPhone - The user's phone number
	 * @param {string} [params.loginMethod] - The method used for login (e.g., 'sms', 'google')
	 */
	function stitchUserSessions({
		userId,
		userName,
		userEmail,
		userPhone,
		loginMethod
	}: {
		userId: string;
		userName: string;
		userEmail: string;
		userPhone: string;
		loginMethod?: string;
	}) {
		const anonymousId = $mixpanel.getAnonymousId();

		if (anonymousId) {
			$mixpanel.aliasUser(userId, anonymousId);

			$mixpanel.track(MixpanelEvents.IDENTITY_MERGE, {
				distinct_id: userId,
				$anon_id: anonymousId,
				user_name: userName,
				user_email: userEmail,
				user_phone: userPhone,
				login_method: loginMethod
			});
		}

		$mixpanel.register({
			user_id: userId,
			user_name: userName,
			user_email: userEmail,
			user_phone: userPhone
		});
	}

	/**
	 * Initialize anonymous tracking
	 *
	 * This should be called when the app first loads to ensure
	 * anonymous tracking is properly set up.
	 */
	function initAnonymousTracking() {
		if (user.value?.id) {
			return;
		}

		const anonymousId = $mixpanel.getAnonymousId();

		if (!anonymousId) {
			console.warn('No anonymous ID found for Mixpanel tracking');
		}
	}

	return {
		stitchUserSessions,
		initAnonymousTracking
	};
}
